import { ObjectLiteral } from '@prophecy/interfaces/generic';

const HINT_KEY = 'prophecy.sso.lastLoginWasSso';
const ATTEMPT_KEY = 'prophecy.sso.attempt';
const LOGOUT_KEY = 'prophecy.sso.logoutPending';
const DISABLED_WINDOW_KEY = '__prophecy-sso-auto-login-disabled';
const FLAG = '1';

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setFlag = (key: string) => {
  try {
    localStorage.setItem(key, FLAG);
  } catch {
    /* a flag we cannot store just means no auto-login */
  }
};

const clearFlag = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing to clear */
  }
};

const hasFlag = (key: string) => read(key) === FLAG;

export const hasSsoLoginHint = () => hasFlag(HINT_KEY);
export const setSsoLoginHint = () => setFlag(HINT_KEY);
export const clearSsoLoginHint = () => clearFlag(HINT_KEY);

export const markSsoAttempt = () => setFlag(ATTEMPT_KEY);

export const clearSsoState = () => {
  clearFlag(HINT_KEY);
  clearFlag(ATTEMPT_KEY);
};

export const markPendingSsoLogout = () => setFlag(LOGOUT_KEY);
export const hasPendingSsoLogout = () => hasFlag(LOGOUT_KEY);
export const clearPendingSsoLogout = () => clearFlag(LOGOUT_KEY);

// Reads the session, not a callback param, so it holds for connectors that send no success flag.
export const resolveSsoAttempt = (isLoggedIn: boolean) => {
  if (!hasFlag(ATTEMPT_KEY)) return;
  clearFlag(ATTEMPT_KEY);
  if (isLoggedIn) setSsoLoginHint();
  else clearSsoLoginHint();
};

// Expiry is when the IDP session is most likely alive, so the 401 wipe must not cost the hint.
export const preserveSsoLoginHint = (clear: () => void) => {
  const hadHint = hasSsoLoginHint();
  clear();
  if (hadHint) setSsoLoginHint();
};

// On `window`, not a module variable, which can be duplicated across chunks; resolved per call so
// nothing touches `window` at import time.
const windowStore = () => window as ObjectLiteral;

export const setSsoAutoLoginDisabled = (value: unknown) => {
  windowStore()[DISABLED_WINDOW_KEY] = value === true || value === 'true';
};

// Unset means enabled, so a missing config never silently loses the feature.
export const isSsoAutoLoginEnabled = () => windowStore()[DISABLED_WINDOW_KEY] !== true;
